// @ts-nocheck
import { CELL, CHUNK, MIN_HOLE_SEPARATION } from "./constants";
import { mapState } from "./state";
import { getHolePos, genChunk } from "./chunks";
import { ensureHoleInDOM, getHoleWorldPosition, getHeadstoneWorldPosition } from "./holes";
import { update } from "./camera";
import { coordsFromHoleId } from "../utils/hole-id";
import { showToast } from "../components/toast/toast";
import { t } from "../i18n";

const HOME_HOLE_IDS = [
  "home_t1",
  "home_t2",
  "home_t3",
  "home_b1",
  "home_b2",
  "home_b3",
];

const SEARCH_RINGS = 50;
let lastFoundHoleId: string | null = null;

function isHoleOccupied(holeId: string) {
  if (mapState.gravesData[holeId]) return true;
  const [x, y] = coordsFromHoleId(holeId);
  const coordKey = `${x},${y}`;
  if (coordKey !== holeId && mapState.gravesData[coordKey]) return true;
  const el = mapState.holeEls[holeId];
  return el?.classList.contains("bought") ?? false;
}

function dist2(ax: number, ay: number, bx: number, by: number) {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

// Bucketed index of every known headstone's *world* position (not just its
// grid holeId), so a candidate that lands within MIN_HOLE_SEPARATION of a
// real memorial gets rejected even when the two don't share the same grid
// cell. Mirrors the backend's isWorldPositionOccupied check (see
// pethernity_be/src/lib/headstone-coords.ts) which is what ultimately
// rejects the checkout — without this, the client could keep proposing the
// same overlapping spot forever.
function buildGraveWorldIndex() {
  const buckets = new Map<string, { x: number; y: number }[]>();
  for (const holeId of Object.keys(mapState.gravesData)) {
    if (!mapState.gravesData[holeId]) continue;
    const [gx, gy] = coordsFromHoleId(holeId);
    const pos = getHeadstoneWorldPosition(gx, gy);
    const bx = Math.floor(pos.x / MIN_HOLE_SEPARATION);
    const by = Math.floor(pos.y / MIN_HOLE_SEPARATION);
    const key = bx + "," + by;
    let arr = buckets.get(key);
    if (!arr) {
      arr = [];
      buckets.set(key, arr);
    }
    arr.push({ x: pos.x, y: pos.y });
  }
  return buckets;
}

function overlapsGraveIndex(
  buckets: Map<string, { x: number; y: number }[]>,
  wx: number,
  wy: number,
) {
  const minDistSq = MIN_HOLE_SEPARATION * MIN_HOLE_SEPARATION;
  const bx = Math.floor(wx / MIN_HOLE_SEPARATION);
  const by = Math.floor(wy / MIN_HOLE_SEPARATION);
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const arr = buckets.get(bx + dx + "," + (by + dy));
      if (!arr) continue;
      for (let i = 0; i < arr.length; i++) {
        const ddx = wx - arr[i].x;
        const ddy = wy - arr[i].y;
        if (ddx * ddx + ddy * ddy < minDistSq) return true;
      }
    }
  }
  return false;
}

function collectEmptyCandidates(excludeIds: Set<string> | null) {
  const candidates: { holeId: string; x: number; y: number }[] = [];
  const seen = new Set<string>();
  const camX = mapState.camX;
  const camY = mapState.camY;
  const graveIndex = buildGraveWorldIndex();

  function add(holeId: string, x: number, y: number) {
    if (excludeIds?.has(holeId)) return;
    if (isHoleOccupied(holeId)) return;
    if (overlapsGraveIndex(graveIndex, x, y)) return;
    if (seen.has(holeId)) return;
    seen.add(holeId);
    candidates.push({ holeId, x, y });
  }

  for (const hid of HOME_HOLE_IDS) {
    const pos = getHoleWorldPosition(hid);
    if (pos) add(hid, pos.x, pos.y);
  }

  const centerGx = Math.round(camX / CELL);
  const centerGy = Math.round(camY / CELL);

  for (let r = 0; r <= SEARCH_RINGS; r++) {
    for (let gx = centerGx - r; gx <= centerGx + r; gx++) {
      for (let gy = centerGy - r; gy <= centerGy + r; gy++) {
        if (
          r > 0 &&
          Math.abs(gx - centerGx) !== r &&
          Math.abs(gy - centerGy) !== r
        ) {
          continue;
        }
        const p = getHolePos(gx, gy);
        if (!p) continue;
        add(gx + "," + gy, p.x, p.y);
      }
    }
  }

  return candidates;
}

type AllocateEmptyHoleOptions = {
  focusMap?: boolean;
  random?: boolean;
  excludeIds?: string[];
};

function sortByDistanceToCamera(
  candidates: { holeId: string; x: number; y: number }[],
) {
  const camX = mapState.camX;
  const camY = mapState.camY;
  return [...candidates].sort(
    (a, b) => dist2(a.x, a.y, camX, camY) - dist2(b.x, b.y, camX, camY),
  );
}

function shuffle<T>(items: T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Random pool size: big enough to give real variety between attempts, small
// enough that the memorial still lands somewhere near the current view.
const RANDOM_POOL_SIZE = 15;

function orderCandidates(
  candidates: { holeId: string; x: number; y: number }[],
  random: boolean,
) {
  const sorted = sortByDistanceToCamera(candidates);
  if (!random) return sorted;
  return shuffle(sorted.slice(0, RANDOM_POOL_SIZE)).concat(
    sorted.slice(RANDOM_POOL_SIZE),
  );
}

function tryReserveCandidate(
  target: { holeId: string; x: number; y: number },
  focusMap: boolean,
) {
  const parts = target.holeId.split(",");
  if (parts.length === 2) {
    const gx = parseInt(parts[0], 10);
    const gy = parseInt(parts[1], 10);
    if (!isNaN(gx) && !isNaN(gy)) {
      genChunk(Math.floor(gx / CHUNK), Math.floor(gy / CHUNK));
    }
  }

  // getHolePos / candidate scan can propose spots that fail spatial placement
  // (tooCloseToAnyHole). Skip those and keep searching.
  const hole = ensureHoleInDOM(target.holeId);
  if (!hole) return null;

  if (focusMap) {
    mapState.camX = target.x;
    mapState.camY = target.y;
    update();
    hole.classList.add("pulse");
    setTimeout(() => hole.classList.remove("pulse"), 900);
  }

  mapState.selectedHole = hole;
  lastFoundHoleId = target.holeId;
  return hole;
}

function pickAndReserveEmptyHole(options: AllocateEmptyHoleOptions = {}) {
  const { focusMap = false, random = false, excludeIds } = options;

  const exclude = new Set<string>(excludeIds ?? []);
  if (lastFoundHoleId) exclude.add(lastFoundHoleId);

  let candidates = orderCandidates(collectEmptyCandidates(exclude), random);

  if (!candidates.length && exclude.size) {
    candidates = orderCandidates(collectEmptyCandidates(null), random);
  }

  for (const target of candidates) {
    const hole = tryReserveCandidate(target, focusMap);
    if (hole) return hole;
  }

  showToast(t("map.noEmptyNearby"), "info");
  return null;
}

/** Pan the map to the nearest empty spot (legacy / window API). */
export function findEmptySpace() {
  return pickAndReserveEmptyHole({ focusMap: true });
}

/**
 * Reserve a free spot without moving the camera. Picked randomly among the
 * nearby empty candidates (not deterministically "the nearest one") so
 * concurrent purchases spread out instead of repeatedly racing for the same
 * spot, and so a retry after a coordinate conflict actually gets a different
 * spot. Pass `excludeIds` to rule out spots that just failed at checkout.
 */
export function allocateEmptyHole(options: { excludeIds?: string[] } = {}) {
  return pickAndReserveEmptyHole({
    focusMap: false,
    random: true,
    excludeIds: options.excludeIds,
  });
}
